import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Frame } from '../components/Frame';
import { FilmTable } from '../components/FilmTable';
import { useKeptFinds } from '../components/FilmCard';
import { useToast } from '../components/Toast';
import { getFilm, rollFilm, filmWanted, drawPoster, listCharacters, type FilmStatus, type Character } from '../lib/crew';
import { listMyPosts, type FeedPost } from '../lib/feed';
import { loadProfile, saveProfile } from '../lib/state';
import { shareLink } from '../lib/share';
import { SLOTS, REQUIRED, CREDIT, type Slot } from '../data/film';
import { collectPieces, currentPieces, picksToSave, toPick, pieceKey, type FilmPiece } from '../lib/pieces';
import { track } from '../analytics';
import { withoutHeld } from '../lib/reveal';
import { useViewed } from '../lib/viewed';

/**
 * The first film (core loop doc 20 §7, #117): the table, then Roll it. The
 * table (K33) is where the row is arranged: spin a reel, seat a piece from
 * the hand, pin what must stay. The crew write one shot from the seated
 * pieces on the backend and H3 Max rolls ten seconds of it; it lands here and
 * on the feed. The first roll is free. The second is a fake door: counted,
 * never granted, until purchase is designed against research/05 (owner,
 * 2026-09-21).
 */
export function Film() {
  useViewed('film');
  const nav = useNavigate();
  const [profile, setProfile] = useState(loadProfile);
  const kept = useKeptFinds();
  const [posts, setPosts] = useState<FeedPost[]>([]);
  const [chars, setChars] = useState<Character[]>([]);
  const [loads, setLoads] = useState(0);
  const [status, setStatus] = useState<FilmStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [drawing, setDrawing] = useState(false);
  const [door, setDoor] = useState(false);
  const { toast, show } = useToast();
  const polls = useRef(0);

  const load = async () => { try { setStatus(await getFilm()); } catch { /* offline: the buttons say so */ } };
  useEffect(() => {
    load();
    listMyPosts(40).then(setPosts).catch(() => {}).finally(() => setLoads((n) => n + 1));
    listCharacters().then(setChars).catch(() => {}).finally(() => setLoads((n) => n + 1));
  }, []);
  const loaded = loads >= 2 && kept !== null;

  const collection = useMemo(() => collectPieces(kept || [], chars, withoutHeld(posts), profile.pencilled), [kept, chars, posts, profile.pencilled]);
  const picks = profile.film?.picks;
  const current = useMemo(() => currentPieces(collection, picks), [collection, picks]);
  useEffect(() => {
    if (!loaded) return;
    const p = loadProfile();
    const next = picksToSave(current, p.film?.picks);
    if (next) saveProfile({ ...p, film: { ...(p.film || {}), picks: next } });
  }, [current, loaded]);
  const seat = (slot: Slot, piece: FilmPiece) => {
    const p = loadProfile();
    const np = { ...p, film: { ...(p.film || {}), picks: { ...(p.film?.picks || {}), [slot]: toPick(piece) } } };
    saveProfile(np); setProfile(np);
  };

  // A roll out with fal: look every five seconds for up to five minutes.
  const pending = status?.film?.status === 'pending';
  useEffect(() => {
    if (!pending) { polls.current = 0; return; }
    const t = setTimeout(() => { if (++polls.current < 60) load(); }, 5000);
    return () => clearTimeout(t);
  }, [pending, status]);

  const roll = async () => {
    setBusy(true);
    try {
      const out = await rollFilm();
      track('film_rolled', { postId: out.postId });
      show('Rolling. A minute or two.');
      await load();
    } catch (err) {
      const code = (err as { code?: string }).code;
      track('film_roll_refused', { code });
      if (code === 'roll_paid') setDoor(true);
      else if (code === 'not_ready') show('The film still needs a piece or two.');
      else if (code === 'out_of_ink') show('Your crew is out of ink today. Try again tomorrow.');
      else if (code === 'crew_off') show('Your crew is off. Turn them on in Friends.');
      else show('That did not start. Try again in a moment.');
    } finally { setBusy(false); }
  };
  /** The crew draw the row as it stands; the picks go with the ask so the drawing matches the table on screen. */
  const draw = async () => {
    setDrawing(true);
    try {
      const out = await drawPoster(picksToSave(current) ?? undefined);
      setStatus((s) => (s ? { ...s, poster: out.poster } : s));
      track('film_poster_drawn', { cached: out.cached });
      if (out.cached) show('Already drawn for this row.');
    } catch (err) {
      const code = (err as { code?: string }).code;
      track('film_poster_refused', { code });
      if (code === 'not_ready') show('Two pieces on the table first.');
      else if (code === 'poster_limit') show('Your crew have drawn enough posters for today. Tomorrow.');
      else if (code === 'out_of_ink') show('Your crew is out of ink today. Try again tomorrow.');
      else if (code === 'crew_off') show('Your crew is off. Turn them on in Friends.');
      else if (code === 'blocked') show('That one would not draw. Try another piece.');
      else show('That did not come out. Try again in a moment.');
    } finally { setDrawing(false); }
  };
  const wantAnother = async () => {
    try { await filmWanted(); track('film_wanted'); show('Counted. Thank you; that helps decide what gets built.'); } catch { show('Could not do that just now.'); }
    setDoor(false);
  };
  const share = async () => {
    const film = status?.film;
    if (!film?.videoUrl) return;
    const outcome = await shareLink(film.videoUrl, `${film.title} · my first film, rolled by my crew from three things I made`);
    track('film_shared', { outcome });
    if (outcome === 'copied') show('Link copied.');
    if (outcome === 'unsupported') show(`Could not open a share sheet here. The link: ${film.videoUrl}`);
  };

  const film = status?.film;
  const missing = REQUIRED.filter((s) => !current[s]);
  const ready = missing.length === 0;
  const seated = SLOTS.filter((s) => current[s]).length;
  // the clip sits in the poster's place only while the table still holds the row it was rolled from; move a piece and the table is a new film in the making (a roll from before the table has no key, so it sits below)
  const sameRow = !!film?.rolledKey && film.rolledKey === pieceKey(current);
  const showClip = film?.status === 'done' && !!film.videoUrl;
  const title = film?.status === 'done' && sameRow ? film.title : ready ? (film ? 'Roll another.' : 'Roll it.') : 'Nearly a film.';

  return (
    <Frame back theme="tabletop" left={<span className="tag">Your first film</span>}>
      <div className="stack" style={{ gap: 4 }}>
        <span className="label" style={{ color: 'var(--green-dark)' }}>{film?.status === 'pending' ? 'Your crew are rolling it' : film?.status === 'done' && sameRow ? 'Rolled by your crew' : `${seated} of ${SLOTS.length} pieces`}</span>
        <h1>{film?.status === 'pending' ? 'Your crew are rolling it.' : title}</h1>
      </div>

      {film?.status === 'pending' && (
        <div className="stack" style={{ gap: 8, alignItems: 'center' }}>
          <video className="permission-art" src="/art/crew-at-work.mp4" poster="/art/crew-at-work.jpg" autoPlay muted loop playsInline aria-label="Your crew at work" />
          <div className="dots"><span className="dot pulse" /><span className="dot pulse d2" /><span className="dot pulse d3" /></div>
          <span className="hint" style={{ textAlign: 'center' }}>A minute or two. It lands here and on your feed; you can leave.</span>
        </div>
      )}

      {film?.status === 'failed' && (
        <p className="why">The clip did not come out this time{film.error === 'out_of_ink' ? ': your crew are out of ink today' : ''}. Nothing was spent; roll it again when you like.</p>
      )}

      {/* the rolled film sits where the poster was; the table below is still yours to arrange for the next roll */}
      <FilmTable mode="table" pieces={collection} current={current} onPick={seat} next={ready ? null : missing[0]} kicker={film?.status === 'done' && sameRow ? film.title : undefined} poster={showClip && sameRow ? { url: film!.posterUrl, title: film!.title, videoUrl: film!.videoUrl } : null} drawn={status?.poster ?? null} onDraw={draw} drawing={drawing} />

      {showClip && (
        <div className="stack" style={{ gap: 10 }}>
          {!sameRow && (
            <div className="stack" style={{ gap: 6 }}>
              <span className="label">Your first roll · {film!.title}</span>
              <video className="film-video" src={film!.videoUrl} poster={film!.posterUrl} controls playsInline preload="metadata" />
            </div>
          )}
          <div className="tagrow"><span className="tag-crew">Rolled by your crew · from your pieces, nothing else</span></div>
          <div className="row" style={{ gap: 10 }}>
            <button className="btn small butter" style={{ flex: 1 }} onClick={share}>Share it</button>
            <a className="btn small ghost" style={{ flex: 1, textAlign: 'center' }} href={film.videoUrl} download="kindling-first-film.mp4">Save it</a>
          </div>
        </div>
      )}

      {!ready && (
        <div className="stack" style={{ gap: 6 }}>
          <p className="why">A film needs three things: someone, somewhere, and a want. Still to find: <b>{missing.map((s) => CREDIT[s].label.toLowerCase()).join(', ')}</b>. Tap an empty reel, or the exercises on Today are aimed at them.</p>
          <button className="btn" onClick={() => nav('/')}>Find the next piece</button>
        </div>
      )}

      {ready && film?.status !== 'pending' && (
        <div className="stack" style={{ marginTop: 'auto', gap: 8 }}>
          {!film && <p className="why" style={{ textAlign: 'center' }}>Ten seconds, from what is on the table. Your crew write the shot from your words; nothing you did not make goes in.</p>}
          <button className="btn" onClick={roll} disabled={busy}>{busy ? 'One moment' : film ? 'Roll another' : 'Roll it · 1 free'}</button>
        </div>
      )}

      {door && (
        <div className="lightbox" onClick={() => setDoor(false)}>
          <div className="sheet" style={{ gap: 10, maxWidth: 360 }} onClick={(e) => e.stopPropagation()}>
            <span className="label">Another roll</span>
            <h2>Another roll costs real ink.</h2>
            <p className="why">The first one was on the house. Packs are not built yet; if you would pay for another, say so and it counts toward building them.</p>
            <button className="btn" onClick={wantAnother}>I’d pay for another</button>
            <button className="btn ghost" onClick={() => setDoor(false)}>Not now</button>
          </div>
        </div>
      )}
      {toast}
    </Frame>
  );
}
